import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Filter,
  LayoutGrid,
  List,
  Calendar,
  User,
  Loader2,
  FileText,
  X,
  Kanban,
  Zap,
  ClipboardList,
  Check,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { mattersApi, clientsApi, usersApi, PaginatedResponse } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import { PRACTICE_AREA_OPTIONS } from "../onboarding/steps/PracticeAreaStep";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import QuickCreateClientModal from "../ui/QuickCreateClientModal";
import SampleBadge from "../ui/SampleBadge";
import CreatePracticeAreaCaseModal from "../practice-areas/CreatePracticeAreaCaseModal";
import CreateImmigrationCaseModal from "../immigration/CreateImmigrationCaseModal";
import CreateRealEstateMatterWizard from "../real-estate/CreateRealEstateMatterWizard";
import { PRACTICE_AREA_CONFIGS, PRACTICE_AREA_TITLES } from "../practice-areas/practiceAreaConfigs";
import PracticeAreaTabContent from "../practice-areas/PracticeAreaPageTemplate";
import ImmigrationPage from "../immigration/ImmigrationPage";
import { useGuidedTour } from "../../contexts/GuidedTourContext";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";
import { errorMessage, notifyError } from "../ui/Toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Matter {
  _id: string;
  matterNumber: string;
  title: string;
  practiceArea: string;
  practiceAreas?: string[];
  status: string;
  stage?: string;
  clientId: { _id: string; displayName: string; type: string } | string;
  responsibleLawyerId: { _id: string; name: string; email: string } | string;
  assignedUserIds: { _id: string; name: string }[];
  openedAt: string;
  closedAt: string | null;
  tags: string[];
  isSample?: boolean;
}

// ─── Color maps ───────────────────────────────────────────────────────────────

const practiceAreaColors: Record<string, string> = {
  real_estate: "bg-cyan-100 text-cyan-700",
  immigration: "bg-emerald-100 text-emerald-700",
  litigation: "bg-blue-100 text-blue-700",
  corporate: "bg-purple-100 text-purple-700",
  family: "bg-pink-100 text-pink-700",
  criminal: "bg-red-100 text-red-700",
  wills_estates: "bg-indigo-100 text-indigo-700",
  personal_injury: "bg-orange-100 text-orange-700",
  employment_labour: "bg-amber-100 text-amber-700",
  intellectual_property: "bg-yellow-100 text-yellow-700",
  tax: "bg-lime-100 text-lime-700",
  environmental: "bg-green-100 text-green-700",
  bankruptcy_insolvency: "bg-rose-100 text-rose-700",
  administrative: "bg-slate-100 text-slate-700",
  construction: "bg-stone-100 text-stone-700",
  insurance: "bg-sky-100 text-sky-700",
  entertainment: "bg-fuchsia-100 text-fuchsia-700",
  technology: "bg-violet-100 text-violet-700",
  class_action: "bg-teal-100 text-teal-700",
  other: "bg-slate-100 text-slate-600",
};

const statusColors: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  on_hold: "bg-amber-100 text-amber-700",
};

const practiceAreaTabColors: Record<string, string> = {
  all: "#0F5132",
  real_estate: "#0891b2",
  immigration: "#059669",
  litigation: "#2563eb",
  corporate: "#9333ea",
  family: "#db2777",
  criminal: "#dc2626",
  wills_estates: "#4f46e5",
  personal_injury: "#ea580c",
  employment_labour: "#d97706",
  intellectual_property: "#ca8a04",
  tax: "#65a30d",
  environmental: "#16a34a",
  bankruptcy_insolvency: "#e11d48",
  administrative: "#475569",
  construction: "#78716c",
  insurance: "#0284c7",
  entertainment: "#c026d3",
  technology: "#7c3aed",
  class_action: "#0d9488",
};

const statusColumns = [
  { key: "open", label: "Active", color: "#059669" },
  { key: "on_hold", label: "On Hold", color: "#d97706" },
  { key: "closed", label: "Closed", color: "#64748b" },
];

// ─── Flow selector: what detailed setup includes per practice area ────────────
const DETAILED_FLOW_FEATURES: Record<string, string[]> = {
  real_estate: [
    "Transaction type & jurisdiction",
    "Property details & legal description",
    "All parties & opposing counsel",
    "Key dates & APS terms",
    "Financial details & LTT",
    "Document checklist",
    "Billing & invoicing",
  ],
  immigration: [
    "Jurisdiction selection",
    "Case type & details",
    "Fees & disbursements",
    "Milestone tracking",
  ],
  _default: [
    "Case type selection",
    "Billing configuration",
    "Invoice setup",
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPracticeArea = (pa: string) =>
  pa
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = { open: "Active", on_hold: "On Hold", closed: "Closed" };
  return labels[status] || status;
};

const formatStatus = (s: string) => getStatusLabel(s);

const getClientName = (c: any) => (typeof c === "object" ? c?.displayName : "Unknown");
const getLawyerName = (l: any) => (typeof l === "object" ? l?.name : "Unknown");

// ─── Matter Card (Cards view) ─────────────────────────────────────────────────

interface MatterCardProps {
  matter: Matter;
  onClick: () => void;
}

const MatterCard: React.FC<MatterCardProps> = React.memo(({ matter, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 cursor-pointer transition-all p-4"
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-[10px] font-mono text-slate-400">
            {matter.matterNumber}
          </p>
          {matter.isSample && <SampleBadge />}
        </div>
        <h4 className="text-sm font-semibold text-slate-800 truncate">{matter.title}</h4>
      </div>
      <span
        className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
          statusColors[matter.status] || "bg-slate-100 text-slate-600"
        }`}
      >
        {formatStatus(matter.status)}
      </span>
    </div>

    <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
      <User className="h-3 w-3 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
      <span className="truncate">{getClientName(matter.clientId)}</span>
    </div>

    <div className="flex flex-wrap gap-1 mb-3">
      {(matter.practiceAreas || [matter.practiceArea]).filter(Boolean).map((pa) => (
        <span
          key={pa}
          className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            practiceAreaColors[pa] || "bg-slate-100 text-slate-600"
          }`}
        >
          {formatPracticeArea(pa)}
        </span>
      ))}
    </div>

    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Briefcase className="h-3 w-3" />
        <span className="truncate">{getLawyerName(matter.responsibleLawyerId)}</span>
      </div>
      {matter.openedAt && (
        <div className="flex items-center gap-1 text-[10px] text-slate-400">
          <Calendar className="h-3 w-3" />
          {new Date(matter.openedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </div>
      )}
    </div>
  </div>
));

MatterCard.displayName = "MatterCard";

// ─── Main Page ────────────────────────────────────────────────────────────────

const MattersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firmData, userData } = useUserDataContext();
  const { tour, setCreatedEntities, advanceStep, setPaused } = useGuidedTour();
  const { openMatterInspector } = useMatterInspector();

  // ── Tab state ────
  const activeTab = searchParams.get("tab") || "all";

  const tabs = useMemo(() => {
    const firmAreas = (firmData as any)?.practiceAreas as string[] | undefined;
    const areaList = firmAreas && firmAreas.length > 0 ? firmAreas : [];
    return [
      { key: "all", label: "All" },
      ...areaList.map((key) => ({
        key,
        label: PRACTICE_AREA_TITLES[key] || formatPracticeArea(key),
      })),
    ];
  }, [firmData]);

  // ── "All" tab state ────
  const [matters, setMatters] = useState<Matter[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [search, setSearch] = useState("");
  const [practiceAreaFilter, setPracticeAreaFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"cards" | "table" | "kanban">("kanban");
  const [lawyerFilter, setLawyerFilter] = useState("");
  const [teamMembers, setTeamMembers] = useState<{_id: string; name: string}[]>([]);
  const [showAreaSelector, setShowAreaSelector] = useState(false);
  const [selectedCreateArea, setSelectedCreateArea] = useState<string | null>(null);
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [showQuickCreateClient, setShowQuickCreateClient] = useState(false);
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  const [pendingArea, setPendingArea] = useState<string | null>(null);

  // For "other" create form
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Build practice area options from firm settings
  const practiceAreaOptions = useMemo(() => {
    const firmAreas = (firmData as any)?.practiceAreas as string[] | undefined;
    const customAreas = (firmData as any)?.customPracticeAreas as string[] | undefined;

    // If firm has selected areas, filter to those + custom
    if (firmAreas && firmAreas.length > 0) {
      const options = PRACTICE_AREA_OPTIONS.filter((a) => firmAreas.includes(a.value));
      // Add custom areas
      if (customAreas) {
        for (const ca of customAreas) {
          options.push({ value: ca, label: ca, description: "", icon: Briefcase as any, color: "bg-gray-50", iconColor: "text-gray-500" });
        }
      }
      return options;
    }

    return PRACTICE_AREA_OPTIONS;
  }, [firmData]);

  // Fetch team members on mount for lawyer filter
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const res = await usersApi.list({ limit: 100 });
        setTeamMembers((res.data.data || []).map((u: any) => ({ _id: u._id, name: u.name })));
      } catch (err) {
        console.error("Failed to fetch team members:", err);
      }
    };
    fetchTeamMembers();
  }, []);

  const fetchMatters = useCallback(async (showLoadingSpinner = false) => {
    if (showLoadingSpinner) setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (practiceAreaFilter) params.practiceArea = practiceAreaFilter;
      if (statusFilter) params.status = statusFilter;
      if (lawyerFilter) params.lawyerId = lawyerFilter;

      const res = await mattersApi.list(params);
      const data = res.data as PaginatedResponse<Matter>;
      setMatters(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    } finally {
      setLoading(false);
    }
  }, [search, practiceAreaFilter, statusFilter, lawyerFilter, pagination.page, pagination.limit]);

  const isMountedRef = React.useRef(false);
  useEffect(() => {
    if (activeTab !== "all") return;
    // Show full loading spinner only on first mount; subsequent fetches do silent refresh
    fetchMatters(!isMountedRef.current ? true : false);
    isMountedRef.current = true;
  }, [fetchMatters, activeTab]);

  // Debounced search  -  use a ref to skip the mount fire
  const [searchInput, setSearchInput] = useState("");
  const searchMountedRef = React.useRef(false);
  useEffect(() => {
    if (!searchMountedRef.current) {
      searchMountedRef.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Kanban helpers ────
  const groupedByStatus = useMemo(() => {
    const grouped: Record<string, Matter[]> = { open: [], on_hold: [], closed: [] };
    for (const m of matters) {
      if (grouped[m.status]) grouped[m.status].push(m);
      else grouped["open"].push(m);
    }
    return grouped;
  }, [matters]);

  useEffect(() => {
    if (viewMode === "kanban") {
      setPagination(prev => ({ ...prev, limit: 200, page: 1 }));
    } else {
      setPagination(prev => ({ ...prev, limit: 50, page: 1 }));
    }
  }, [viewMode]);

  const handleKanbanDragEnd = useCallback(async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    const newStatus = destination.droppableId;
    const prevMatters = [...matters];

    // Optimistic update
    setMatters(prev => prev.map(m => m._id === draggableId ? { ...m, status: newStatus } : m));

    try {
      await mattersApi.changeStatus(draggableId, newStatus);
    } catch (err) {
      console.error("Failed to update status:", err);
      setMatters(prevMatters);
    }
  }, [matters]);

  // Fetch clients & users for create modal
  const loadFormData = async () => {
    try {
      const [cRes, uRes] = await Promise.all([
        clientsApi.list({ limit: 100, excludeSample: true }),
        usersApi.list({ limit: 100 }),
      ]);
      setClients(cRes.data.data || []);
      setUsers(uRes.data.data || []);
    } catch (err) {
      console.error("Failed to load form data:", err);
      notifyError("Couldn't load clients and team members for the new matter form.", err);
    }
  };

  // ── Create flow ────
  const [otherFormData, setOtherFormData] = useState({
    title: "",
    clientId: "",
    responsibleLawyerId: userData._id || "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Closing a creation modal without creating pauses the tour rather than
  // ending it, so the spotlight returns to the New Matter button.
  const resumeTourAfterClose = () => {
    if (tour.isActive) setPaused(false);
  };

  const closeOtherModal = () => {
    setShowOtherModal(false);
    setPendingArea(null);
    setCreateError("");
    resumeTourAfterClose();
  };

  const handleOpenCreate = () => {
    if (tour.isActive) setPaused(true);
    setShowAreaSelector(true);
  };

  const handleAreaSelected = (areaValue: string) => {
    setShowAreaSelector(false);
    if (areaValue === "other") {
      // "Other" has no detailed wizard, go straight to simple form
      setPendingArea("other");
      loadFormData();
      setOtherFormData({ title: "", clientId: "", responsibleLawyerId: userData._id || "" });
      setCreateError("");
      setShowOtherModal(true);
    } else if (PRACTICE_AREA_CONFIGS[areaValue] || areaValue === "immigration") {
      // Show flow selector for areas that have detailed wizards
      setPendingArea(areaValue);
      setShowFlowSelector(true);
    } else {
      // Unknown area, fall back to simple form
      setPendingArea(areaValue);
      loadFormData();
      setOtherFormData({ title: "", clientId: "", responsibleLawyerId: userData._id || "" });
      setCreateError("");
      setShowOtherModal(true);
    }
  };

  const handleQuickCreate = () => {
    setShowFlowSelector(false);
    loadFormData();
    setOtherFormData({ title: "", clientId: "", responsibleLawyerId: userData._id || "" });
    setCreateError("");
    setShowOtherModal(true);
  };

  const handleDetailedCreate = () => {
    setShowFlowSelector(false);
    if (pendingArea === "immigration") {
      setSelectedCreateArea("immigration");
    } else if (pendingArea && PRACTICE_AREA_CONFIGS[pendingArea]) {
      setSelectedCreateArea(pendingArea);
    }
  };

  const handleCreateOther = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    try {
      const res = await mattersApi.create({ ...otherFormData, practiceArea: pendingArea || "other" });
      setShowOtherModal(false);

      if (tour.isActive && tour.currentStep === 1) {
        const client = clients.find((c: any) => c._id === otherFormData.clientId);
        setCreatedEntities({
          matterId: res.data._id,
          clientId: otherFormData.clientId,
          clientDisplayName: client?.displayName || "",
          matterTitle: otherFormData.title,
        });
        advanceStep();
        fetchMatters();
      } else {
        navigate(`/matters/${res.data._id}`);
      }
    } catch (err: any) {
      console.error("Failed to create matter:", err);
      setCreateError(errorMessage(err, "Couldn't create the matter. Please try again."));
    } finally {
      setCreating(false);
    }
  };

  const handleSpecializedCreated = (matterId: string, meta?: { clientId: string; clientDisplayName: string; matterTitle: string }) => {
    setSelectedCreateArea(null);
    fetchMatters();

    if (tour.isActive && tour.currentStep === 1 && meta) {
      setCreatedEntities({
        matterId,
        clientId: meta.clientId,
        clientDisplayName: meta.clientDisplayName,
        matterTitle: meta.matterTitle,
      });
      advanceStep();
    } else {
      navigate(`/matters/${matterId}`);
    }
  };

  const hasActiveFilters = practiceAreaFilter || statusFilter || lawyerFilter;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Matters</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {activeTab === "all"
              ? `${pagination.total} matter${pagination.total !== 1 ? "s" : ""}`
              : PRACTICE_AREA_TITLES[activeTab] || formatPracticeArea(activeTab)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle — only on All tab */}
          {activeTab === "all" && (
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                onClick={() => setViewMode("cards")}
                className={`p-2 transition-colors ${
                  viewMode === "cards" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
                }`}
                title="Cards view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("kanban")}
                className={`p-2 transition-colors ${
                  viewMode === "kanban" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
                }`}
                title="Kanban view"
              >
                <Kanban className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 transition-colors ${
                  viewMode === "table" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
                }`}
                title="Table view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          )}
          <button
            data-tour="new-matter-btn"
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-5 w-5" />
            New Matter
          </button>
        </div>
      </div>

      {/* Tab bar */}
      {tabs.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4">
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSearchParams(tab.key === "all" ? {} : { tab: tab.key })}
                className="relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap"
                style={{
                  color: activeTab === tab.key ? (practiceAreaTabColors[tab.key] || "var(--color-primary)") : "#64748b",
                }}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <motion.div
                    layoutId="matters-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: practiceAreaTabColors[tab.key] || "var(--color-primary)" }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab Content ──────────────────────────────────────────────────── */}

      {activeTab === "all" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search matters..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <SearchableSelect
              icon={Filter}
              options={practiceAreaOptions.map((a) => ({ value: a.value, label: a.label }))}
              value={practiceAreaFilter || null}
              onChange={(val) => {
                setPracticeAreaFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Practice Areas"
              clearable
            />
            <SearchableSelect
              icon={FileText}
              options={[
                { value: "open", label: "Active" },
                { value: "on_hold", label: "On Hold" },
                { value: "closed", label: "Closed" },
              ]}
              value={statusFilter || null}
              onChange={(val) => {
                setStatusFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Statuses"
              clearable
            />
            <SearchableSelect
              icon={User}
              options={teamMembers.map((m) => ({ value: m._id, label: m.name }))}
              value={lawyerFilter || null}
              onChange={(val) => {
                setLawyerFilter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Lawyers"
              clearable
            />
            {hasActiveFilters && (
              <>
                <div className="h-5 w-px bg-slate-200" />
                {practiceAreaFilter && (
                  <button
                    onClick={() => setPracticeAreaFilter("")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-light text-brand"
                  >
                    {formatPracticeArea(practiceAreaFilter)}
                    <X className="h-3 w-3" />
                  </button>
                )}
                {statusFilter && (
                  <button
                    onClick={() => setStatusFilter("")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-light text-brand"
                  >
                    {formatStatus(statusFilter)}
                    <X className="h-3 w-3" />
                  </button>
                )}
                {lawyerFilter && (
                  <button
                    onClick={() => setLawyerFilter("")}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-light text-brand"
                  >
                    {teamMembers.find((m) => m._id === lawyerFilter)?.name || "Lawyer"}
                    <X className="h-3 w-3" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            </div>
          ) : matters.length === 0 && !search && !hasActiveFilters ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Briefcase className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">No matters yet</h3>
              <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                Create your first matter to start tracking cases, deadlines, and billing all in one place.
              </p>
              <button
                onClick={handleOpenCreate}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                <Plus className="h-5 w-5" />
                Create Matter
              </button>
              <div className="mt-8 flex flex-wrap justify-center gap-4 max-w-lg">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Track deadlines & tasks
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Generate invoices
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Store documents securely
                </div>
              </div>
            </motion.div>
          ) : matters.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 shadow-sm">
                  <Search className="h-8 w-8 text-slate-400" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1">No matters match your search</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Try different keywords or clear your filters to see all matters.
              </p>
            </motion.div>
          ) : viewMode === "cards" ? (
            /* ── Cards View ──────────────────────────────────────────── */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-6">
              {matters.map((matter) => (
                <MatterCard
                  key={matter._id}
                  matter={matter}
                  onClick={() => openMatterInspector(matter._id)}
                />
              ))}
            </div>
          ) : viewMode === "kanban" ? (
            /* ── Kanban View ─────────────────────────────────────────── */
            <DragDropContext onDragEnd={handleKanbanDragEnd}>
              <div className="flex gap-4 overflow-x-auto" style={{ minHeight: "400px" }}>
                {statusColumns.map((col) => (
                  <div key={col.key} className="flex-shrink-0 w-[320px] flex flex-col max-h-[calc(100vh-260px)]">
                    {/* Column header */}
                    <div
                      className="rounded-t-xl px-4 py-3 border-b-2 bg-white flex-shrink-0"
                      style={{ borderBottomColor: col.color }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: col.color }}
                          />
                          <h3 className="text-sm font-semibold text-slate-700">{col.label}</h3>
                        </div>
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${col.color}20`, color: col.color }}
                        >
                          {(groupedByStatus[col.key] || []).length}
                        </span>
                      </div>
                    </div>

                    {/* Droppable area */}
                    <Droppable droppableId={col.key}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto transition-colors duration-200 ${
                            snapshot.isDraggingOver
                              ? "bg-brand-light/70 ring-2 ring-inset ring-brand-light/50"
                              : "bg-slate-50/80"
                          }`}
                        >
                          {(groupedByStatus[col.key] || []).map((matter, index) => (
                            <Draggable key={matter._id} draggableId={matter._id} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  onClick={() => openMatterInspector(matter._id)}
                                  className={`bg-white rounded-xl border cursor-grab active:cursor-grabbing ${
                                    dragSnapshot.isDragging
                                      ? "shadow-2xl border-brand-light ring-2 ring-brand-light scale-[1.02]"
                                      : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
                                  }`}
                                  style={dragProvided.draggableProps.style}
                                >
                                  <div className="p-3.5">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-[10px] font-mono text-slate-400">
                                        {matter.matterNumber}
                                      </p>
                                      {matter.isSample && <SampleBadge />}
                                    </div>
                                    <h4 className="text-sm font-semibold text-slate-800 truncate mb-2">{matter.title}</h4>
                                    <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                                      <User className="h-3 w-3 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
                                      <span className="truncate">{getClientName(matter.clientId)}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {(matter.practiceAreas || [matter.practiceArea]).filter(Boolean).map((pa) => (
                                        <span
                                          key={pa}
                                          className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                            practiceAreaColors[pa] || "bg-slate-100 text-slate-600"
                                          }`}
                                        >
                                          {formatPracticeArea(pa)}
                                        </span>
                                      ))}
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                                      <span className="text-xs text-slate-500 truncate">{getLawyerName(matter.responsibleLawyerId)}</span>
                                      {matter.openedAt && (
                                        <span className="text-[10px] text-slate-400">
                                          {new Date(matter.openedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {(groupedByStatus[col.key] || []).length === 0 && !snapshot.isDraggingOver && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.4, delay: 0.2 }}
                              className="flex flex-col items-center justify-center py-8 text-slate-300"
                            >
                              <div className="p-3 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 mb-2">
                                <Briefcase className="h-5 w-5 text-slate-300" />
                              </div>
                              <span className="text-xs font-medium text-slate-400">Drag matters here</span>
                            </motion.div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          ) : (
            /* ── Table View ──────────────────────────────────────────── */
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                        Matter/File #
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                        Title
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                        Client
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                        Practice Area
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">
                        Lawyer
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">
                        Opened
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {matters.map((matter) => (
                      <tr
                        key={matter._id}
                        onClick={() => openMatterInspector(matter._id)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-mono text-slate-500">
                          {matter.matterNumber}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{matter.title}</span>
                            {matter.isSample && <SampleBadge />}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                          {getClientName(matter.clientId)}
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(matter.practiceAreas || [matter.practiceArea]).filter(Boolean).map((pa) => (
                              <span
                                key={pa}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  practiceAreaColors[pa] || "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {formatPracticeArea(pa)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              statusColors[matter.status] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {formatStatus(matter.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden xl:table-cell">
                          {getLawyerName(matter.responsibleLawyerId)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 hidden xl:table-cell">
                          {matter.openedAt
                            ? new Date(matter.openedAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : " - "}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {viewMode !== "kanban" && pagination.pages > 1 && (
            <div className="flex items-center justify-between px-2 py-4 mt-2">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "immigration" && (
        <ImmigrationPage />
      )}

      {activeTab !== "all" && activeTab !== "immigration" && (
        <PracticeAreaTabContent
          key={activeTab}
          title={PRACTICE_AREA_TITLES[activeTab] || formatPracticeArea(activeTab)}
          icon={(practiceAreaOptions.find((a) => a.value === activeTab)?.icon || Briefcase) as React.FC<any>}
          practiceAreaKey={activeTab}
          pipelineType={activeTab}
          accentHex={practiceAreaTabColors[activeTab]}
          caseConfig={PRACTICE_AREA_CONFIGS[activeTab]}
        />
      )}

      {/* ── Modals (always in shell) ─────────────────────────────────────── */}

      {/* Step 1: Practice Area Selector Modal */}
      <PremiumModal
        open={showAreaSelector}
        onClose={() => { setShowAreaSelector(false); resumeTourAfterClose(); }}
        title="New Matter"
        subtitle="Select a practice area"
        icon={Briefcase}
        size="lg"
      >
        <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1">
          {practiceAreaOptions.map((area) => {
            const Icon = area.icon;
            return (
              <button
                key={area.value}
                type="button"
                onClick={() => handleAreaSelected(area.value)}
                className="flex items-start gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-brand-accent hover:bg-brand-light/30 text-left transition-all"
              >
                <div
                  className={`h-9 w-9 rounded-lg ${area.color} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`h-4 w-4 ${area.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">
                    {area.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                    {area.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </PremiumModal>

      {/* Step 2: Flow Selector — Quick Create vs Detailed Setup */}
      <PremiumModal
        open={showFlowSelector}
        onClose={() => { setShowFlowSelector(false); setPendingArea(null); resumeTourAfterClose(); }}
        title={`New ${PRACTICE_AREA_TITLES[pendingArea || ""] || formatPracticeArea(pendingArea || "")} Matter`}
        subtitle="Choose how you'd like to get started"
        icon={practiceAreaOptions.find((a) => a.value === pendingArea)?.icon || Briefcase}
        size="md"
      >
        <div className="grid grid-cols-2 gap-4">
          {/* Quick Create Card */}
          <motion.button
            type="button"
            onClick={handleQuickCreate}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="group relative flex flex-col items-start text-left p-5 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all"
          >
            <div className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>

            <h3 className="text-base font-bold text-slate-800 mb-1">Quick Create</h3>
            <p className="text-xs text-slate-500 mb-4">Get started in seconds</p>

            <div className="space-y-2.5 mb-4 w-full">
              {["Matter title", "Client", "Responsible team member"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-auto flex items-center gap-1.5 text-xs text-slate-400">
              <Clock className="h-3 w-3" />
              Add details anytime later
            </div>

            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <ArrowRight className="h-4 w-4 text-emerald-500" />
            </div>
          </motion.button>

          {/* Detailed Setup Card */}
          <motion.button
            type="button"
            onClick={handleDetailedCreate}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="group relative flex flex-col items-start text-left p-5 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all"
          >
            <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
              <ClipboardList className="h-5 w-5 text-emerald-600" />
            </div>

            <h3 className="text-base font-bold text-slate-800 mb-1">Detailed Setup</h3>
            <p className="text-xs text-slate-500 mb-4">Set up everything upfront</p>

            <div className="space-y-2.5 mb-4 w-full">
              {(DETAILED_FLOW_FEATURES[pendingArea || ""] || DETAILED_FLOW_FEATURES._default).map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-auto flex items-center gap-1.5 text-xs text-slate-400">
              <ClipboardList className="h-3 w-3" />
              Complete setup in one go
            </div>

            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <ArrowRight className="h-4 w-4 text-emerald-500" />
            </div>
          </motion.button>
        </div>
      </PremiumModal>

      {/* Specialized Practice Area Modal */}
      {selectedCreateArea && selectedCreateArea !== "immigration" && selectedCreateArea !== "real_estate" && PRACTICE_AREA_CONFIGS[selectedCreateArea] && (
        <CreatePracticeAreaCaseModal
          config={PRACTICE_AREA_CONFIGS[selectedCreateArea]}
          title={PRACTICE_AREA_TITLES[selectedCreateArea] || selectedCreateArea}
          icon={(practiceAreaOptions.find((a) => a.value === selectedCreateArea)?.icon || Briefcase) as React.FC<any>}
          onClose={() => { setSelectedCreateArea(null); resumeTourAfterClose(); }}
          onCreated={handleSpecializedCreated}
        />
      )}

      {/* Immigration Modal */}
      {selectedCreateArea === "immigration" && (
        <CreateImmigrationCaseModal
          onClose={() => { setSelectedCreateArea(null); resumeTourAfterClose(); }}
          onCreated={(caseId, meta) => {
            setSelectedCreateArea(null);
            fetchMatters();
            if (tour.isActive && tour.currentStep === 1 && meta) {
              setCreatedEntities({
                matterId: caseId,
                clientId: meta.clientId,
                clientDisplayName: meta.clientDisplayName,
                matterTitle: meta.matterTitle,
              });
              advanceStep();
            } else {
              navigate(`/immigration/${caseId}`);
            }
          }}
        />
      )}

      {/* Real Estate Wizard */}
      {selectedCreateArea === "real_estate" && (
        <CreateRealEstateMatterWizard
          onClose={() => { setSelectedCreateArea(null); resumeTourAfterClose(); }}
          onCreated={(caseId, meta) => {
            setSelectedCreateArea(null);
            fetchMatters();
            if (tour.isActive && tour.currentStep === 1 && meta) {
              setCreatedEntities({
                matterId: meta.matterId || caseId,
                clientId: meta.clientId,
                clientDisplayName: meta.clientDisplayName,
                matterTitle: meta.matterTitle,
              });
              advanceStep();
            } else {
              navigate(`/matters/${meta?.matterId || caseId}`);
            }
          }}
        />
      )}

      {/* Quick Create / "Other" Simple Create Modal */}
      <PremiumModal
        open={showOtherModal}
        onClose={closeOtherModal}
        title={pendingArea && pendingArea !== "other" ? `Quick Create ${PRACTICE_AREA_TITLES[pendingArea] || formatPracticeArea(pendingArea)}` : "New Matter"}
        subtitle={pendingArea && pendingArea !== "other" ? "Just the basics, add details later" : "General / Other"}
        icon={pendingArea && pendingArea !== "other" ? (practiceAreaOptions.find((a) => a.value === pendingArea)?.icon || Briefcase) : Briefcase}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeOtherModal}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateOther as any}
              disabled={creating || !otherFormData.title || !otherFormData.clientId || !otherFormData.responsibleLawyerId}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </span>
              ) : "Create Matter"}
            </button>
          </div>
        }
      >
        <form onSubmit={handleCreateOther} className="space-y-5">
          {createError && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{createError}</span>
            </div>
          )}
          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Matter Details</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                required
                autoFocus
                value={otherFormData.title}
                onChange={(e) => setOtherFormData({ ...otherFormData, title: e.target.value })}
                className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="Smith v. Johnson"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">People</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <SearchableSelect
              options={clients.map((c) => ({ value: c._id, label: c.displayName }))}
              value={otherFormData.clientId || null}
              onChange={(val) => setOtherFormData({ ...otherFormData, clientId: val || "" })}
              placeholder="Select a client"
              required
              icon={User}
              addNew={{
                label: "Add new client",
                onAdd: () => setShowQuickCreateClient(true),
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Responsible Team Member *</label>
            <SearchableSelect
              options={users.map((u) => ({ value: u._id, label: u.name }))}
              value={otherFormData.responsibleLawyerId || null}
              onChange={(val) => setOtherFormData({ ...otherFormData, responsibleLawyerId: val || "" })}
              placeholder="Select a team member"
              required
            />
          </div>
        </form>
      </PremiumModal>

      <QuickCreateClientModal
        open={showQuickCreateClient}
        onClose={() => setShowQuickCreateClient(false)}
        onCreated={(newClient) => {
          setClients((prev: any[]) => [...prev, newClient]);
          setOtherFormData((prev) => ({ ...prev, clientId: newClient._id }));
          setShowQuickCreateClient(false);
        }}
      />
    </div>
  );
};

export default MattersPage;
